import { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';
import { useViewScope } from '@/shared/hooks/use-view-scope';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';
import { Button } from '@/shared/components/ui/button';

interface SuperAdminGuardProps {
  children: ReactNode;
}

export function SuperAdminGuard({ children }: SuperAdminGuardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSuperAdmin, isLoading } = useIsSuperAdminRole();
  const { scope } = useViewScope();
  const { clearSelectedOrganization } = useSuperAdminOrg();
  const { setCurrentWorkspace } = useWorkspace();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return <Navigate to="/access-denied" />;
  }

  // Rotas /admin/* exigem platform scope. Em vez de redirecionar e descartar trabalho não salvo,
  // mostra um aviso com ação para voltar à Visão Superadmin.
  if (scope !== 'platform') {
    const handleBackToPlatform = () => {
      clearSelectedOrganization();
      setCurrentWorkspace(null);
    };

    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="max-w-md w-full rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-6 text-center space-y-4">
          <div className="flex justify-center">
            <div className="rounded-full bg-amber-100 dark:bg-amber-900/60 p-3">
              <ShieldAlert className="h-6 w-6 text-amber-700 dark:text-amber-300" />
            </div>
          </div>
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
              {t('navigation.superadminOnlyTitle', 'Área exclusiva da Visão Superadmin')}
            </h2>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {t('navigation.superadminOnlyDescription', 'Esta área só está disponível quando nenhuma organização está selecionada.')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button onClick={handleBackToPlatform}>
              {t('navigation.backToPlatform', 'Voltar para Visão Superadmin')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              {t('navigation.goToDashboard', 'Ir para o Dashboard')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
